import React, { useContext, useMemo, useState, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { TextField, Autocomplete, InputAdornment } from "@mui/material";
import { ServiceContext } from "../../../data/services/ServiceContext";
import { ActionContext } from "../../../data/action/ActionContext";
import { SubActionContext } from "../../../data/action/SubActionContext";
import { PersonelContext } from "../../../data/personel/PersonelContext";
import { useValidations } from "../../../data/ValidationContext";

function ServiceActionUpdateModal({ show, handleClose, action }) {
  const service = action;

  const { updateService } = useContext(ServiceContext);
  const { actions } = useContext(ActionContext);
  const { subActions } = useContext(SubActionContext);
  const { personelList } = useContext(PersonelContext);

  const { vs_serviceAction } = useValidations(); 
  const assignedAction = useMemo(
    () => (service ? actions.find((a) => a.id === service.actionId) : null),
    [service, actions]
  );

  const availableSubActions = useMemo(() => {
    if (!assignedAction) return [];
    return subActions.filter((sa) => assignedAction.subActions.includes(sa.id));
  }, [assignedAction, subActions]);

  const [selectedSubAction, setSelectedSubAction] = useState(null);

  // Ortak alanlar
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [personnel, setPersonnel] = useState(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [workDone, setWorkDone] = useState("");
  const [offer, setOffer] = useState("");
  const [callTime, setCallTime] = useState("");
  const [price, setPrice] = useState("");

  const [errors, setErrors] = useState({}); 
  

  useEffect(() => {
    if (!service) return;
    const currentSub = service.subActionId
      ? subActions.find((sa) => sa.id === service.subActionId)
      : null;
    setSelectedSubAction(currentSub || null);

    setNote(service.extraData?.note || "");
    setAmount(service.extraData?.amount || "");
    setPersonnel(service.extraData?.personnel || null);
    setPaymentDate(service.extraData?.paymentDate || "");
    setWorkDone(service.extraData?.workDone || "");
    setOffer(service.extraData?.offer || "");
    setCallTime(service.extraData?.callTime || "");
    setPrice(service.extraData?.price || "");
  }, [service, subActions]);

  if (!service) return null;

  const handleSave = async () => {
    const payload = {
      subActionId: selectedSubAction ? selectedSubAction.id : undefined,
      extraData: {
        note,
        amount,
        personnel,
        paymentDate,
        workDone,
        offer,
        callTime,
        price,
      },
    };

    try {
      await vs_serviceAction.validate(payload, {
        abortEarly: false,
        context: { subActionName: selectedSubAction?.name || "" },    });

      updateService(service.id, payload);
      handleClose();
    } catch (err) {
      if (err.inner) {
        const newErrors = {};
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors); 
      }
    }
  };

  // Saat aralığı seçenekleri
  const callTimeOptions = [
    "09:00-10:00",
    "10:00-11:00",
    "11:00-12:00",
    "12:00-13:00",
    "13:00-14:00",
    "14:00-15:00",
    "15:00-16:00",
  ];

  // SubAction bazlı form
  const renderDynamicFields = () => {
    if (!selectedSubAction) return null;

    switch (selectedSubAction.name) {
      case "Haber Verecek":
      case "Müşteri İptal Etti":
      case "Teyid Araması":
      case "Cihaz Tamir Edilemiyor":
      case "Şikayetçi":
      case "Yeniden Teknisyen Yönlendir":
      case "Servis Sonlandırıldı":
      case "Parçası Atölyeye Alındı":
      case "Cihaz Teslim Edildi":
      case "Fiyatta Anlaşılamadı":
        return (
          <TextField
            label="Açıklama"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            error={!!errors["extraData.note"]}
            helperText={errors["extraData.note"]}
          />
        );

      case "Tahsilata Gönder":
        return (
          <>
            <Row>
              <Col>
                <TextField
                  label="Ödeme Tarihi"
                  type="date"
                  size="small"
                  fullWidth
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors["extraData.paymentDate"]}
                  helperText={errors["extraData.paymentDate"]}
                />
              </Col>
              <Col>
                <TextField
                  label="Tutar"
                  type="number"
                  size="small"
                  fullWidth
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₺</InputAdornment>,
                  }}
                  error={!!errors["extraData.amount"]}
                  helperText={errors["extraData.amount"]}
                />
              </Col>
            </Row>

            <Autocomplete
              options={personelList}
              value={personnel}
              onChange={(e, newVal) => setPersonnel(newVal)}
              getOptionLabel={(option) => option?.fullName || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Personel"
                  size="small"
                  className="mt-2"
                  error={!!errors["extraData.personnel"]}
                  helperText={errors["extraData.personnel"]}
                />
              )}
              noOptionsText="Personel bulunamadı"
            />
          </>
        );

      case "Yerinde Bakım Yapıldı":
        return (
          <>
            <TextField
              label="Yapılan İşlemler"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={workDone}
              onChange={(e) => setWorkDone(e.target.value)}
              error={!!errors["extraData.workDone"]}
              helperText={errors["extraData.workDone"]}
            />
            <TextField
              label="Verilen Teklif"
              size="small"
              fullWidth
              className="mt-2"
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₺</InputAdornment>,
              }}
              error={!!errors["extraData.offer"]}
              helperText={errors["extraData.offer"]}
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              className="mt-2"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              error={!!errors["extraData.note"]}
              helperText={errors["extraData.note"]}
            />
          </>
        );

      case "Müşteriye Ulaşılamadı":
        return (
          <>
            <Autocomplete
              options={callTimeOptions}
              value={callTime || null}
              onChange={(e, newVal) => setCallTime(newVal)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Arama Saati Aralığı"
                  size="small"
                  error={!!errors["extraData.callTime"]}
                  helperText={errors["extraData.callTime"]}
                />
              )}
              className="mb-2"
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              error={!!errors["extraData.note"]}
              helperText={errors["extraData.note"]}
            />
          </>
        );

      case "Atölyeye Alındı":
      case "Parça Gidecek":
        return (
          <>
            <TextField
              label="Müşteriye Verilen Fiyat"
              type="number"
              size="small"
              fullWidth
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₺</InputAdornment>,
              }}
              error={!!errors["extraData.price"]}
              helperText={errors["extraData.price"]}
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              className="mt-2"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>Servis Durumu Güncelle</Modal.Title>
      </Modal.Header>

      <div className="p-4">
        <div className="mb-3">
          <label className="form-label">Servis Durumu</label>
          <input
            type="text"
            className="form-control"
            value={assignedAction ? assignedAction.name : "Beklemede"}
            disabled
          />
        </div>

        <div className="mb-3">
          <Autocomplete
            options={availableSubActions}
            value={selectedSubAction || null}
            onChange={(e, newVal) => setSelectedSubAction(newVal)}
            getOptionLabel={(option) => option?.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Yapılacak işlem seçin"
                size="small"
                error={!!errors.subActionId}
                helperText={errors.subActionId}
              />
            )}
            noOptionsText={
              assignedAction
                ? "Bu aksiyona bağlı işlem yok."
                : "Önce bir aksiyon atayın."
            }
            disablePortal
            autoHighlight
          />
        </div>

        {/* Dinamik alanlar */}
        <div className="mb-3">{renderDynamicFields()}</div>
      </div>

      <div className="p-3 pt-0 d-flex justify-content-end gap-2">
        <button className="btn btn-primary" onClick={handleSave}>
          Kaydet
        </button>
        <button className="btn btn-secondary" onClick={handleClose}>
          İptal
        </button>
      </div>
    </Modal>
  );
}

export default ServiceActionUpdateModal;
